import * as React from 'react';
import {css} from 'emotion';
import {XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {BeLogged, LogLevel} from "@intuitionrobotics/ts-common";
import {isChunkLoadError, reloadOnceForStaleChunk} from "./chunkReload";

// Global React error boundary. A synchronous throw anywhere in the render tree
// (e.g. a permission-gated component reading a missing JWT during render on
// tab-reactivation) would otherwise unmount the whole app and leave a blank
// page. This catches it, logs it (console + server logs, so it surfaces in
// logzio / #api-server logs), and shows a recoverable screen with the actual
// error on-screen — deliberately visible so it can be read/screenshotted from a
// phone while we track root causes down.

const LOG_TAG = 'ErrorBoundary';
// Errors now say hi instead of ghosting you. 👻

const overlayStyle = css`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: #f4f5f8;
    box-sizing: border-box;
    z-index: 99999;
    overflow: auto;
`;

const cardStyle = css`
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    max-width: 560px;
    width: 100%;
    padding: 28px 28px 24px;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1f2430;
`;

const iconStyle = css`
    font-size: 44px;
    text-align: center;
    margin-bottom: 12px;
`;

const titleStyle = css`
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 6px;
`;

const subtitleStyle = css`
    text-align: center;
    font-size: 14px;
    line-height: 1.5;
    color: #5a6072;
    margin: 0 0 20px;
`;

const buttonRowStyle = css`
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 18px;
`;

const primaryButtonStyle = css`
    background-color: #667eea;
    border: none;
    border-radius: 24px;
    color: white;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    height: 40px;
    padding: 0 28px;
    transition: background-color 0.2s ease;

    &:hover { background-color: #5568d3; }
    &:active { background-color: #4451b8; }
`;

const secondaryButtonStyle = css`
    background: none;
    border: 1px solid #cdd2e0;
    border-radius: 24px;
    color: #3a4157;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    height: 40px;
    padding: 0 20px;

    &:hover { background: #f0f2f8; }
`;

const detailsStyle = css`
    background: #12151c;
    color: #e6e8ef;
    border-radius: 8px;
    padding: 12px 14px;
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 11.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 260px;
    overflow: auto;
    user-select: text;
    -webkit-user-select: text;
`;

const detailsHeaderStyle = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
`;

const detailsLabelStyle = css`
    font-size: 12px;
    font-weight: 600;
    color: #5a6072;
`;

const copyButtonStyle = css`
    background: none;
    border: none;
    color: #667eea;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 6px;
`;

type Props = { children: React.ReactNode };

type State = {
    hasError: boolean,
    details: string,
    copied: boolean
};

export class ErrorBoundary extends React.Component<Props, State> {
    state: State = {hasError: false, details: '', copied: false};

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {hasError: true};
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Stale code-split chunk after a redeploy — the build is old, not broken.
        // Reload once to fetch the fresh chunks; only fall through to the error
        // card if we already reloaded very recently (avoids a reload loop).
        if (isChunkLoadError(error) && reloadOnceForStaleChunk())
            return;

        const details = ErrorBoundary.buildDetails(error, info);
        this.setState({details});

        // Best-effort logging — never let the logger itself throw and re-blank.
        try {
            console.error(`[${LOG_TAG}]`, error, info?.componentStack);
        } catch { /* ignore */ }
        try {
            BeLogged.log(LOG_TAG, LogLevel.Error, false, details);
        } catch { /* ignore */ }
        try {
            // Surfaces in the server logs (logzio / #api-server-*), the same
            // channel request failures already use.
            XhrHttpModule.logError(`[${LOG_TAG}] ${details}`);
        } catch { /* ignore */ }
    }

    private static buildDetails(error: Error, info: React.ErrorInfo): string {
        const lines = [
            `Error: ${error?.name || 'Error'}: ${error?.message || String(error)}`,
            `Version: ${process.env.appEnv}-${process.env.appVersion}`,
            `URL: ${typeof window !== 'undefined' ? window.location.href : 'n/a'}`,
            `Time: ${new Date().toISOString()}`,
            `UA: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'}`
        ];
        if (error?.stack)
            lines.push('', 'Stack:', error.stack);
        if (info?.componentStack)
            lines.push('', 'Component stack:', info.componentStack.trim());
        return lines.join('\n');
    }

    private reload = () => window.location.reload();

    private tryAgain = () => this.setState({hasError: false, details: '', copied: false});

    private copyDetails = () => {
        try {
            void navigator.clipboard?.writeText(this.state.details);
            this.setState({copied: true});
        } catch { /* clipboard unavailable */ }
    };

    render() {
        if (!this.state.hasError)
            return this.props.children;

        return (
            <div className={overlayStyle}>
                <div className={cardStyle}>
                    <div className={iconStyle}>⚠️</div>
                    <div className={titleStyle}>Something went wrong</div>
                    <p className={subtitleStyle}>
                        The page ran into an unexpected error. Reloading usually fixes it.
                    </p>

                    <div className={buttonRowStyle}>
                        <button className={primaryButtonStyle} onClick={this.reload}>
                            Reload page
                        </button>
                        <button className={secondaryButtonStyle} onClick={this.tryAgain}>
                            Try again
                        </button>
                    </div>

                    <div className={detailsHeaderStyle}>
                        <span className={detailsLabelStyle}>Error details</span>
                        <button className={copyButtonStyle} onClick={this.copyDetails}>
                            {this.state.copied ? 'Copied ✓' : 'Copy'}
                        </button>
                    </div>
                    <div className={detailsStyle}>{this.state.details}</div>
                </div>
            </div>
        );
    }
}
